import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../data/site";
import { Reveal } from "./Reveal";

export const Gallery = () => {
  const [open, setOpen] = useState(null);

  const step = useCallback((d) => {
    setOpen((prev) => (prev === null ? prev : (prev + d + GALLERY.length) % GALLERY.length));
  }, []);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step]);

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="gallery-grid">
        {GALLERY.map((img, i) => (
          <Reveal key={img.url} delay={(i % 3) * 0.05}>
            <button
              type="button"
              onClick={() => setOpen(i)}
              data-testid={`gallery-item-${i}`}
              aria-label={`Avaa kuva: ${img.alt}`}
              className="group relative block aspect-square w-full overflow-hidden rounded-2xl bg-[#8A9B87]/10"
            >
              <img
                src={img.url}
                alt={img.alt}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
              />
              <span className="absolute inset-0 bg-[#2E2E2C]/0 transition-colors duration-300 group-hover:bg-[#2E2E2C]/15" />
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#2E2E2C]/95 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setOpen(null)}
            role="dialog"
            aria-modal="true"
            data-testid="gallery-lightbox"
          >
            <button
              type="button"
              onClick={() => setOpen(null)}
              data-testid="lightbox-close"
              aria-label="Sulje kuva"
              className="absolute right-5 top-5 rounded-full border border-[#F7F4EE]/25 p-2 text-[#F7F4EE] transition-colors hover:bg-[#F7F4EE]/10"
            >
              <X size={20} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              data-testid="lightbox-prev"
              aria-label="Edellinen kuva"
              className="absolute left-3 sm:left-8 rounded-full border border-[#F7F4EE]/25 p-2 text-[#F7F4EE] transition-colors hover:bg-[#F7F4EE]/10"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              data-testid="lightbox-next"
              aria-label="Seuraava kuva"
              className="absolute right-3 sm:right-8 rounded-full border border-[#F7F4EE]/25 p-2 text-[#F7F4EE] transition-colors hover:bg-[#F7F4EE]/10"
            >
              <ChevronRight size={22} />
            </button>
            <motion.img
              key={open}
              src={GALLERY[open].url}
              alt={GALLERY[open].alt}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="max-h-[85vh] max-w-[92vw] rounded-xl object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
